import * as THREE from "three";
import Experience from "../../../Experience.js";
import SeededRandom from "../../../Utils/SeededRandom.js";

export default class Seaweed {
    constructor() {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.resources = this.experience.resources;

        this.resource = this.resources.items.seaweedModel;

        this.setModel();
    }

    setModel() {
        // Extract geometry from the resource
        let geometry = null;
        this.resource.scene.traverse((child) => {
            if (child.isMesh && !geometry) {
                geometry = child.geometry;
            }
        });

        if (!geometry) return;

        const numGroups = 20;
        const seaweedsPerGroup = 10;
        const count = numGroups * seaweedsPerGroup;

        this.material = new THREE.MeshPhongMaterial({
            color: 0xffffff, // will be overridden by instance color
            flatShading: true,
            side: THREE.DoubleSide,
        });

        this.material.userData.uniforms = {
            uTimeS: { value: 0 },
        };

        this.material.onBeforeCompile = (shader) => {
            shader.uniforms.uTimeS = this.material.userData.uniforms.uTimeS;

            shader.vertexShader = shader.vertexShader.replace(
                "#include <common>",
                `
                #include <common>
                uniform float uTimeS;
                varying float vLocalZ;
                `,
            );
            shader.vertexShader = shader.vertexShader.replace(
                "#include <begin_vertex>",
                `
                #include <begin_vertex>
                vLocalZ = position.z;
                vec3 worldPos = (modelMatrix * instanceMatrix * vec4(position, 1.0)).xyz;
                
                // Calculate height factor assuming the root is near y = 0
                float heightFactor = max(0.0, position.y);
                
                // Calculate a pseudo-random bend amount per instance
                float randomBend = 0.2 + 0.5 * fract(sin(float(gl_InstanceID) * 78.233) * 43758.5453);
                
                // Because each seaweed is randomly rotated around Y via the instanceMatrix,
                // bending along local Z and X will fan them out from the shared root
                transformed.z += atan(heightFactor * randomBend);
                transformed.x += atan(heightFactor * randomBend);

                // Movements due to water currents - sine wave based on x position
                float phase = worldPos.x * 0.1 + worldPos.z * 0.1;
                transformed.x += mix(0., 0.2, heightFactor / 3.) * sin(uTimeS + heightFactor * 0.5 + phase);
                transformed.z += mix(0., 0.1, heightFactor / 3.) * cos(uTimeS + heightFactor * 0.5 + phase);
                `,
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <common>",
                `
                #include <common>
                varying float vLocalZ;
                `,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <color_fragment>",
                `
                #include <color_fragment>
                // Create a dark vein at z = 0
                float veinMask = smoothstep(0.0, 0.08, abs(vLocalZ));
                diffuseColor.rgb *= mix(0.75, 1.0, veinMask);
                // diffuseColor.rgb = vec3(0.);
                `,
            );
        };

        this.mesh = new THREE.InstancedMesh(geometry, this.material, count);
        this.mesh.name = "seaweed";
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        this.mesh.position.set(0, -20.5, 0);

        // Custom depth material for correct shadows
        const depthMaterial = new THREE.MeshDepthMaterial({
            depthPacking: THREE.RGBADepthPacking,
        });

        depthMaterial.onBeforeCompile = (shader) => {
            shader.uniforms.uTimeS = this.material.userData.uniforms.uTimeS;

            shader.vertexShader = shader.vertexShader.replace(
                "#include <common>",
                `
                #include <common>
                uniform float uTimeS;
                `,
            );
            shader.vertexShader = shader.vertexShader.replace(
                "#include <begin_vertex>",
                `
                #include <begin_vertex>
                vec3 worldPos = (modelMatrix * instanceMatrix * vec4(position, 1.0)).xyz;
                
                float heightFactor = max(0.0, position.y);
                float randomBend = 0.2 + 0.5 * fract(sin(float(gl_InstanceID) * 78.233) * 43758.5453);
                
                transformed.z += atan(heightFactor * randomBend);
                transformed.x += atan(heightFactor * randomBend);

                float phase = worldPos.x * 0.1 + worldPos.z * 0.1;
                transformed.x += mix(0., 0.2, heightFactor / 3.) * sin(uTimeS + heightFactor * 0.5 + phase);
                transformed.z += mix(0., 0.1, heightFactor / 3.) * cos(uTimeS + heightFactor * 0.5 + phase);
                `,
            );
        };

        this.mesh.customDepthMaterial = depthMaterial;

        const dummy = new THREE.Object3D();
        const color = new THREE.Color();
        // const rng = new SeededRandom(12245); // Specific seed for consistent results
        // const rng = new SeededRandom(11764); // Specific seed for consistent results
        const rng = new SeededRandom(11650); // Specific seed for consistent results

        const innerRadius = 40;
        const outerRadius = 110;

        let index = 0;
        for (let i = 0; i < numGroups; i++) {
            // Generate a random angle and distance for the group center
            const angle = rng.random() * Math.PI * 2;
            const distance =
                innerRadius + rng.random() * (outerRadius - innerRadius);

            const groupX = Math.cos(angle) * distance;
            const groupZ = Math.sin(angle) * distance;

            for (let j = 0; j < seaweedsPerGroup; j++) {
                // All seaweeds in a group share the same root coordinates ( with subtle offsets )
                const x = groupX + (rng.random() - 0.5) * 1; // Slight random offset for natural look
                const z = groupZ + (rng.random() - 0.5) * 1; // Slight random offset for natural look

                // Random scale
                const scale = 1.0 + rng.random() * 1.5;

                // Random rotation
                const rotationY = rng.random() * Math.PI * 2;

                dummy.position.set(x, 0, z);
                dummy.rotation.set(0, rotationY, 0);
                dummy.scale.set(scale, scale * 1.5, scale);
                dummy.updateMatrix();

                this.mesh.setMatrixAt(index, dummy.matrix);

                // Various shades of green
                // Base: Forest Green ~ HSL(0.33, 0.76, 0.34)
                const hue = 0.25 + Math.random() * 0.1; // 0.25 to 0.35 (yellow-green to sea-green)
                const saturation = 0.7 + Math.random() * 0.3; // 0.7 to 1.0
                const lightness = 0.2 + Math.random() * 0.3; // 0.2 to 0.5

                color.setHSL(hue, saturation, lightness);
                this.mesh.setColorAt(index, color);

                index++;
            }
        }

        this.mesh.instanceMatrix.needsUpdate = true;
        this.mesh.instanceColor.needsUpdate = true;
    }

    update() {
        // if (this.mesh.material.uniforms.uTimeS) {
        this.material.userData.uniforms.uTimeS.value +=
            this.experience.time.delta * 0.001; // Adjust speed as needed
        // }
    }
}
